// Nature Strip — the frozen contract between the simulation, the renderers,
// the input layer and the shell. Only the orchestrator edits this file; agents
// propose changes in a short note instead.
//
// Rules that follow from it:
//   - src/sim/** is pure TypeScript. No DOM, no Vue, no Ionic, no three
//     (eslint enforces it). It exposes `createSim(...)` → `Sim` with
//     `step(dt, input)` and a read-only `SimState`.
//   - Units are metric: metres for position, mm for grass length, km/h only
//     at display time (the sim works in m/s), litres, AUD.
//   - World space: x grows east (right), y grows south (down), metres, origin
//     at the layout's top-left corner. Heading is radians, 0 = east, π/2 =
//     south (screen-down), so the same numbers drive a top-down canvas and a
//     three.js ground plane (x → x, y → z).

// ─── Grid ────────────────────────────────────────────────────────────────

/** What a cell physically is. Mowable = `grass` only. */
export type CellType = 'grass' | 'paved' | 'bed' | 'obstacle' | 'edge' | 'water' | 'out';
export const CELL_TYPES: readonly CellType[] = ['grass', 'paved', 'bed', 'obstacle', 'edge', 'water', 'out'];

export type GrassType = 'couch' | 'buffalo' | 'kikuyu' | 'weedy';

/**
 * A zone overrides the layout's cut-height range for its cells, or marks them
 * do-not-mow. `none` = the layout default applies.
 */
export type ZoneId = 'none' | 'wicket-square' | 'pitch' | 'green' | 'outfield' | 'nature-strip' | 'dam-edge';
export const ZONE_IDS: readonly ZoneId[] = ['none', 'wicket-square', 'pitch', 'green', 'outfield', 'nature-strip', 'dam-edge'];

export interface ZoneRule {
  id: ZoneId;
  /** Target cut range in mm. Omitted on do-not-mow zones. */
  rangeMm?: [number, number];
  /** Touching this zone with blades on = violation (−A$50 once per contact). */
  doNotMow?: boolean;
  /** Deck step in mm while inside this zone's contract (bowls/wicket = 2). */
  deckStepMm?: number;
  /**
   * Whether touching a doNotMow zone is fined (default true). `false` makes
   * it a plain exclusion: not counted for mowing, no fine (dam edges).
   */
  penalty?: boolean;
}

/**
 * Cell storage is struct-of-arrays for speed: a 120×90 oval is 10,800 cells
 * and the renderer repaints dirty ones every frame. Index = y * w + x.
 */
export interface Grid {
  w: number;
  h: number;
  /** Metres per cell edge (backyard 0.5, oval 1.5). */
  cellM: number;
  /** Index into CELL_TYPES. */
  type: Uint8Array;
  /** Index into ZONE_IDS. */
  zone: Uint8Array;
  /** Grass type per cell (index into GRASS_TYPES); bindii patches are `weedy`. */
  grass: Uint8Array;
  /** Current grass length in mm (grass cells); 0 elsewhere. */
  lengthMm: Float32Array;
  /** Length at job start, kept for the before/after slider. */
  startLengthMm: Float32Array;
  /** 1 once any blade/trimmer has cut this cell this job. */
  cut: Uint8Array;
  /**
   * Heading of the last pass that cut this cell, quantised: 0..255 maps to
   * 0..2π. Renderers derive the lit/shade stripe from it: lit when the pass
   * went away from the light (see STRIPE_LIGHT_HEADING), shade when toward.
   */
  stripe: Uint8Array;
  /**
   * Crossed stripes (SimOptions.crossStripes): the heading of the pass before
   * the last one, kept when the last pass crossed it at 60° or more (a
   * checkerboard's or a diamond's second set). Quantised like `stripe` with the
   * low bit set, so 0 means none. Renderers mix the two passes' lit/shade (the
   * last pass counts more), which gives crossed patterns their four tones.
   * Absent when off.
   */
  stripe2?: Uint8Array;
  /** Weeds on paved cells / fence lines (1 = present). Trimmer clears them. */
  weed: Uint8Array;
  /** Wet cells (sprinkler strip): load ×1.5 while wet. */
  wet: Uint8Array;
  /** 1 = trim cell: grass the mower can't reach (8-adjacent to a solid/edge/bed/out/water cell). Trimmer only. */
  trim: Uint8Array;
  /**
   * Feature P9 (SimOptions.tyreTracks): matted grass under the wheels, 0..255,
   * fading to 0 over TYRE_TRACK_FADE_S. Absent when off. Renderers darken by it.
   */
  matted?: Uint8Array;
  /**
   * Feature P9 (SimOptions.blowBack): 1 on a paved cell the trimmer threw
   * clippings onto; they count against a clear until the blower moves them.
   * Absent when off.
   */
  clip?: Uint8Array;
  /**
   * Feature P9 (SimOptions.passableProps): 1 on a cell loose stone or mulch
   * edging scattered onto when driven over. Visual only. Absent when off.
   */
  scatter?: Uint8Array;
  /**
   * Cell indices changed since the renderer last consumed them. The sim
   * pushes, the renderer drains (sets length 0) after painting. Never read it
   * for game rules.
   */
  dirty: number[];
}

export const GRASS_TYPES: readonly GrassType[] = ['couch', 'buffalo', 'kikuyu', 'weedy'];

/**
 * Stripes read lit when the mower heads this way. Diagonal (up-left, −3π/4) on
 * purpose: with a straight-up light, east and west passes both land at
 * cos(±π/2) = 0 and a back-and-forth job shows no stripes at all. At 45° every
 * cardinal direction and its reverse fall on opposite sides of the light.
 */
export const STRIPE_LIGHT_HEADING = -3 * Math.PI / 4;

// ─── Layouts (content, as JSON) ─────────────────────────────────────────

export type PropKind =
  | 'hills-hoist' | 'trampoline' | 'veggie-bed' | 'shed' | 'dog-bowl' | 'cubby' | 'letterbox'
  | 'wheelie-bin' | 'gum-tree' | 'colorbond-fence' | 'goal-post' | 'boundary-fence' | 'bench'
  | 'canteen' | 'scoreboard' | 'sight-screen' | 'covers' | 'rope' | 'playground' | 'shade-sail'
  | 'sandpit' | 'verandah' | 'annexe' | 'car' | 'camp-kitchen' | 'headstone' | 'water-tank'
  | 'sheep' | 'pool' | 'sprinkler' | 'flower-bed' | 'tap' | 'power-pit' | 'rock' | 'ute' | 'caravan'
  /** A dwelling's roof over its `out` cells (real yards). Solid, rect footprint, penalty false. */
  | 'house'
  /** Front fences ("Adjust your yard"): non-solid decoration over `e` cells (picket, low brick) or `b` cells (hedge). */
  | 'picket-fence' | 'brick-fence' | 'hedge'
  /** Feature P9: low things the mower rolls over (sim/props.ts PROP_TRAITS: passable; stone edging also scatters). */
  | 'garden-hose' | 'brick-edging' | 'stone-edging';

/**
 * What a mower hit costs (Feature P1). `minor` is the long-standing
 * OBSTACLE_PENALTY_AUD, `major` is MAJOR_HIT_PENALTY_AUD (car, ute, pool),
 * `none` is a soft edge that blocks but never bills. Catalogue: sim/props.ts.
 */
export type DamageOnHit = 'none' | 'minor' | 'major';

export interface Prop {
  id: string;
  kind: PropKind;
  /** Centre, metres. */
  x: number;
  y: number;
  /** Footprint in metres (rect), or `r` for circles. */
  w?: number;
  h?: number;
  r?: number;
  /** Radians. */
  rot?: number;
  /** Collides with the mower. Non-solid props are decoration (shade sail, rope overhead…). */
  solid: boolean;
  /** Renderer keys (2D sprite / 3D mesh factory). Default = kind. */
  sprite?: string;
  mesh?: string;
  /** Hitting it costs A$15 (default true for solid props). */
  penalty?: boolean;
  /** Overrides the kind's catalogue entry (sim/props.ts PROP_DAMAGE). Wins over `penalty`. */
  damageOnHit?: DamageOnHit;
  /** Visual radius when bigger than the solid footprint (Hills hoist arms, tree canopy). Renderers only. */
  canopyR?: number;
  /**
   * Feature P6: a deck or step footprint; leaves resting on it count against a
   * clear. Overrides the kind's catalogue entry (sim/props.ts PROP_TRAITS).
   */
  debrisSurface?: boolean;
  /**
   * Feature P9 (SimOptions.passableProps): the mower rolls over it with a bump,
   * no stop and no deduction. Overrides the kind's catalogue entry (small rocks
   * are passable by size, sim/props.ts isPassable).
   */
  passable?: boolean;
  /** Feature P9: passable and scatters loose stone/mulch onto nearby cells when driven over (visual only). */
  scatter?: boolean;
}

export interface Spawn {
  x: number;
  y: number;
  heading: number;
}

export interface Layout {
  key: string;
  title: string;
  /** Street-directory suburb: 'Kerbside', 'Gumtree Heights', 'Federation Drive', 'Coolamon Bay', or a real one for real yards and Home turf. */
  suburb: string;
  locationGroup: LocationGroup;
  grassType: GrassType;
  cellM: number;
  /**
   * Time compression for big layouts. The mower's deck width and speed are
   * multiplied by this in world metres (ovals ≈ 3.5, so a Redgum Rider 96
   * covers ~2 cells of 1.5 m and a footy oval takes 8–12 min, not 2 h).
   * Default 1. Par time uses the scaled numbers, so it stays fair.
   */
  gameScale?: number;
  w: number;
  h: number;
  /**
   * Row strings, one char per cell: `g` grass, `p` paved, `b` bed, `o`
   * obstacle, `e` edge (fence line), `w` water, `.` out of bounds. Upper-case
   * `G` = grass with the `weedy` override (bindii/clover patch).
   */
  rows: string[];
  /** Optional zone map, same shape: `.` none, `W` wicket-square, `P` pitch, `G` green, `O` outfield, `N` nature-strip, `D` dam-edge. */
  zoneRows?: string[];
  zones: ZoneRule[];
  /** Default target cut range for grass cells with no zone. */
  rangeMm: [number, number];
  /** Initial uncut length range mm (random per cell by seed). */
  startLengthMm: [number, number];
  props: Prop[];
  spawn: Spawn;
  /** Where the ute parks (tool switch happens anywhere, this is flavour + 3D). */
  utePark?: Spawn;
  /** Paved cells that start with weeds, as fraction 0..1. */
  weedFraction: number;
  /** Leaf/bark count range, and where they fall: 'all' or a list of rects (m). */
  leaves: { count: [number, number]; areas?: Array<{ x: number; y: number; w: number; h: number }> };
  /** Renderer hints. */
  camera: { fit: 'contain' | 'follow'; zoom?: number; allowLandscape: boolean };
  /** Mutations that may apply on a visit (keys into the mutation table). */
  mutations: MutationKey[];
  /**
   * Feature P9 (SimOptions.slopeDrift): which way the ground falls, as an
   * acceleration-like vector (m/s² equivalent, world axes). The mower drifts
   * that way at SLOPE_DRIFT_GAIN × |slope| × gameScale m/s at full speed. No HUD.
   */
  slope?: { x: number; y: number };
  /**
   * Render-only surroundings (look-dev Phase 5). The sim never reads it; the
   * 3D view draws what's beyond the lot from it. Real yards only.
   */
  context?: LayoutContext;
}

/** What's around a real lot, in the layout's world frame (metres, x right, y down). */
export interface LayoutContext {
  /**
   * Building footprints near the lot that aren't on it (the neighbours), from
   * the same Overture/OSM data as the lot's own buildings. `height` (m, to the
   * roof top) when the data has one.
   */
  buildings: Array<{ poly: Array<[number, number]>; height?: number }>;
}

export type LocationGroup = 'backyards' | 'nature-strips' | 'ovals' | 'clubs' | 'parks' | 'rural';

export type MutationKey =
  | 'veggie-bed' | 'above-ground-pool' | 'trampoline' | 'bin-night' | 'magpie-season'
  | 'sprinkler' | 'bindii' | 'heavy-leaves' | 'ibis' | 'lost-ball' | 'hot-day';

// ─── Equipment & economy ────────────────────────────────────────────────

export type ToolKind = 'mower' | 'trimmer' | 'blower' | 'rake' | 'tarp';
export type Brand = 'Redgum' | 'Coolabah' | 'Jarrah Iron' | 'Kookaburra Tools';

/** Feature P2: discharge chute side, relative to the direction of travel. */
export type DischargeSide = 'left' | 'right' | 'none';

export interface EquipmentModel {
  key: string;             // 'redgum-46'
  name: string;            // 'Redgum 46'
  brand: Brand;
  kind: ToolKind;
  priceAud: number;
  /** Mowers: deck width in cm. Trimmer: cut radius cm. Blower: cone length cm. */
  deckCm?: number;
  /** Top speed km/h (mowers); walking tools use the walk speed. */
  speedKmh?: number;
  /** Motor load tolerance divisor (mowers). */
  tolerance?: number;
  power: 'petrol' | 'battery' | 'none';
  /** L/h at full throttle (petrol). */
  burnLph?: number;
  tankL?: number;
  /**
   * Bags what it picks up. A mower: a catcher (clippings and loose leaves). A
   * blower: a garden vac, which sucks leaves in over a short reach
   * (`deckCm`) instead of blowing them (tools/blower.ts).
   */
  collector?: boolean;
  /**
   * Feature P2: which side the discharge chute throws clippings (and sticks
   * out ~0.15 m). 'none' = a catcher. Absent: 'none' with a collector, else 'right'.
   */
  dischargeSide?: DischargeSide;
  rideOn?: boolean;
  /**
   * Walk-behind mowers with a drive (the SP models): the mower pulls itself
   * along and you steer. Ride-ons and zero-turns always drive themselves and
   * don't set it. Absent reads as false.
   */
  selfPropelled?: boolean;
  /**
   * Battery hand tools: seconds of trigger time on a full charge (the pack
   * size). Absent reads as BATTERY_RUN_S (economy.ts), which every battery
   * tool used before the equipment range (docs/equipment.md).
   */
  runS?: number;
  /** Mower silhouette for renderers. */
  style?: 'push' | 'ride-on' | 'zero-turn';
  /** Shop tier = rep level needed to buy. */
  tier: number;
  blurb: string;
}

/** One owned item. */
export interface OwnedEquipment {
  uid: string;
  modelKey: string;
  engineCondition: number; // 0..1
  bladeCondition: number;  // 0..1
  fuelL: number;
  /**
   * Feature P7: battery tools' charge, 0–100 (they have no fuel). Absent on
   * saves from before it and on petrol/hand gear; absent reads as full
   * (economy.chargeOf, migrated by economy.migrateCharge).
   */
  chargePct?: number;
}

// ─── Contracts & scoring ────────────────────────────────────────────────

export type TaskKind = 'ground-check' | 'mow' | 'trim' | 'blow' | 'collect';

export interface TaskSpec {
  kind: TaskKind;
  /** Target percentage (0..100) to count as done. */
  target: number;
  label: string;
}

export interface Contract {
  id: string;
  layoutKey: string;
  seed: number;
  mutations: MutationKey[];
  tasks: TaskSpec[];
  complexity: 1 | 2 | 3 | 4 | 5;
  feeAud: number;
  estMinutes: number;
  /** Copy line for the card, e.g. "Kikuyu's gone feral out the back." */
  blurb: string;
  /** Rep level required to see it. */
  minRepLevel: number;
  /**
   * Feature P3: a one-line story from the pool of 40 (seeded by the contract),
   * shown on the card and the loadout. Absent on boards built with the flag off.
   */
  story?: string;
  /**
   * Feature P5: show the full headland lap (and island laps) as a dashed guide
   * while mowing. The shell stamps it on the job's copy at start for the
   * player's first two contracts; renderers read it off `state.contract`.
   */
  headlandHint?: boolean;
  /**
   * Feature P8: on a yard the player has worked before, the 1–2 mutations the
   * revisit added, none of them there last time (the card's "New since last
   * time: …"). Absent on a first visit and on boards built with the flag off.
   */
  newSince?: MutationKey[];
  /**
   * Feature P9: the yard hasn't been mowed in months (board option
   * `neglected`): the fee already carries ×NEGLECTED_FEE_MULT and createSim
   * doubles the uncut length and slows the mower in it. Absent otherwise.
   */
  neglected?: boolean;
}

export interface TaskProgress {
  kind: TaskKind;
  label: string;
  /** 0..100 */
  pct: number;
  target: number;
  done: boolean;
}

export interface JobResult {
  contractId: string;
  layoutKey: string;
  abandoned: boolean;
  seconds: number;
  parSeconds: number;
  stars: 0 | 1 | 2 | 3;
  tasks: TaskProgress[];
  /** Mean task pct, 0..100. */
  thoroughness: number;
  feeAud: number;       // fee earned (pro-rata when abandoned)
  tipPct: number;
  tipAud: number;
  obstacleHits: number;
  violations: number;   // pitch / green / do-not-mow contacts
  /** Ground objects mowed over (MULCH_PENALTY_AUD each, included in penaltiesAud). */
  mulched?: number;
  /** Their kinds, in object order — for the "Mulched the {kind}" invoice lines. */
  mulchedKinds?: GroundObject['kind'][];
  /** Charged prop hits in order (Feature P1): kind and whether it billed minor or major. */
  hits?: { kind: PropKind; damage: Exclude<DamageOnHit, 'none'>; via?: 'chute' }[];
  /** The do-not-mow zone of each violation, in order (Feature P1). */
  violationZones?: ZoneId[];
  penaltiesAud: number;
  totalAud: number;
  repPoints: number;
  /** Fuel used etc. for the running-costs line. */
  fuelUsedL: number;
  /** Owned gear as it comes home (fuel, wear) — economy.applyJobResult writes it back. */
  gear?: OwnedEquipment[];
  /** Feature P5: stripe quality 0–100 (sim/stripes.ts). Absent when nothing was mowed to rate. */
  stripeScore?: number;
  /**
   * The pattern the stripes read as (sim/stripes.ts stripePattern), with
   * SimOptions.crossStripes on: plain stripes, double-wide, diagonal,
   * checkerboard, diamond, the sports hatch or rings. A pattern other than
   * plain stripes, done tidily, adds a point or two to the stripe bonus.
   * Absent when the option is off or nothing was mowed to rate.
   */
  stripePattern?: 'stripes' | 'double' | 'diagonal' | 'checker' | 'diamond' | 'hatch' | 'rings';
  /**
   * Feature P5: tip points paid on top of `tipPct` for the stripes (0, 1, 3 or 5,
   * plus 1 or 2 for a tidy named pattern: sim/stripes.ts patternBonusPct).
   * Absent until the bonus is applied (`withStripeBonus`, or `result(…, { stripeBonus: true })`);
   * when present, `totalAud` includes `stripeBonusAud`.
   */
  stripeBonusPct?: number;
  /** Feature P5: the stripe bonus in dollars, round(fee × stripeBonusPct / 100). */
  stripeBonusAud?: number;
  /**
   * Feature P6: leaves blown off the property on a clear (`blow`) job, and the
   * thoroughness points that cost the clear task (OFF_PROPERTY_NUDGE_PCT).
   * Absent without debris rules or when nothing went over the fence.
   */
  offProperty?: { leaves: number; nudgePct: number };
  /**
   * Feature P9 (SimOptions.groundItems): A$ for wallets handed back, the
   * "honest bloke" line (HONEST_BLOKE_AUD each). Included in `totalAud`.
   */
  honestBlokeAud?: number;
  /** Feature P9: how many wallets that was. */
  wallets?: number;
  /**
   * Feature P9 (SimOptions.extraMile): the one-off goodwill for work beyond the
   * contract (EXTRA_MILE_AUD, and EXTRA_MILE_REP already in `repPoints`).
   * Included in `totalAud`. Absent when not earned.
   */
  extraMileAud?: number;
  extraMileRep?: number;
  /** Feature P9: display names of the mulched objects (named ground items), parallel to `mulchedKinds`. */
  mulchedNames?: string[];
}

// ─── Live sim state ─────────────────────────────────────────────────────

export interface Operator {
  /** Metres. */
  x: number;
  y: number;
  heading: number;
  /** Current speed m/s. */
  speed: number;
  /** Active tool. */
  tool: ToolKind;
  /** On a mower (riding/pushing) vs walking with a hand tool. */
  onMower: boolean;
  /** The active tool is running this frame (blades on / trimmer / blower held). */
  toolActive: boolean;
}

export interface MowerState {
  modelKey: string;
  /** Deck width in cells (rounded, ≥1) and metres. */
  deckCells: number;
  deckM: number;
  /** Deck height (cut height) mm. */
  deckMm: number;
  deckStepMm: number;
  bladesOn: boolean;
  cruise: boolean;
  stalled: boolean;
  /** 0..~1.3; 0.85 = amber line, 1.0 = red line. */
  load: number;
  /** Seconds above 0.85 / at ≥1.0, for the condition + stall rules. */
  highLoadS: number;
  redLoadS: number;
  engineCondition: number; // 0..1
  bladeCondition: number;  // 0..1
  fuelL: number;
  tankL: number;
  power: 'petrol' | 'battery' | 'none';
  /** Mower parked position when the operator walks off with a hand tool. */
  parkedX: number;
  parkedY: number;
  parkedHeading: number;
  /** Feature P2: the brought mower's chute side, for the renderers' clippings spray. */
  dischargeSide?: DischargeSide;
}

export interface GroundObject {
  id: string;
  kind: 'stick' | 'newspaper' | 'cricket-ball' | 'footy' | 'school-hat' | 'drink-bottle' | 'wallet' | 'brick' | 'tennis-ball' | 'frisbee' | 'dog-toy';
  x: number;
  y: number;
  state: 'ground' | 'collected' | 'stolen' | 'mulched';
  /**
   * Feature P9 (SimOptions.groundItems): the named item it is. Items with no
   * kind of their own (thong, sunnies) ride on a stand-in `kind` for renderers
   * that don't know them (sim/vibe.ts GROUND_ITEM_KIND).
   */
  item?: GroundItem;
}

/** Feature P9: the named ground-check items. */
export type GroundItem = 'stick' | 'newspaper' | 'thong' | 'cricket-ball' | 'footy' | 'wallet' | 'dog-toy' | 'sunnies';

/**
 * Leaves/bark/clippings. Struct-of-arrays; count is hundreds, not thousands.
 * state: 0 loose, 1 locked on tarp, 2 bagged, 3 blown off-property.
 */
export interface Leaves {
  count: number;
  x: Float32Array;
  y: Float32Array;
  vx: Float32Array;
  vy: Float32Array;
  kind: Uint8Array;   // 0 gum leaf, 1 bark strip, 2 clippings
  state: Uint8Array;
}

/** x/y are the tarp's CENTRE (same as props). */
export interface Tarp {
  placed: boolean;
  x: number; y: number; w: number; h: number;
  /** Leaves locked on it. */
  held: number;
  capacity: number;
  bagged: number;
}

export type SimEvent =
  | { type: 'task-complete'; task: TaskKind }
  | { type: 'stall' }
  | { type: 'restart' }
  | { type: 'obstacle-hit'; propId: string; kind: PropKind; damage?: Exclude<DamageOnHit, 'none'>; via?: 'chute' }
  | { type: 'object-mulched'; objectId: string }
  | { type: 'object-collected'; objectId: string }
  | { type: 'violation'; zone: ZoneId }
  | { type: 'magpie'; x: number; y: number }
  | { type: 'ibis'; objectId: string }
  | { type: 'sprinkler-on' }
  | { type: 'bagged'; count: number }
  | { type: 'out-of-fuel' }
  /** Feature P7: a battery hand tool dropped to LOW_CHARGE_PCT (once per job per tool). */
  | { type: 'low-charge'; tool: ToolKind }
  /** Feature P7: a battery hand tool went flat; it won't run again this job. */
  | { type: 'flat'; tool: ToolKind }
  /** Feature P9: rolled over a passable prop (a bump, no stop, no charge); `scatter` when it threw stones/mulch. */
  | { type: 'bump'; propId: string; kind: PropKind; scatter?: boolean }
  /** Feature P9: the zero-turn's first pivot on the spot this job (the shell shows its tip once). */
  | { type: 'zero-turn-pivot' }
  | { type: 'all-done' };

export interface SimState {
  readonly layout: Layout;
  readonly contract: Contract;
  readonly grid: Grid;
  readonly op: Operator;
  readonly mower: MowerState;
  /** Tools on the ute for this job. */
  readonly loadout: ToolKind[];
  readonly objects: GroundObject[];
  readonly leaves: Leaves;
  readonly tarp: Tarp;
  readonly tasks: TaskProgress[];
  /** Mowable cell count / cells in range right now. */
  readonly mowableCells: number;
  readonly inRangeCells: number;
  /** Trim-only cells (ring cells the mower can't reach) — for par time and trim %. */
  readonly trimCells: number;
  readonly elapsedS: number;
  readonly parS: number;
  readonly obstacleHits: number;
  readonly violations: number;
  /** Heat stress 0..1 on hot-day walking sections. */
  readonly heat: number;
  readonly finished: boolean;
  /** Events emitted during the last step(); cleared at the start of each step. */
  readonly events: SimEvent[];
  /** Monotonic, bumps each step — renderers can skip identical frames. */
  readonly tick: number;
  /** Feature P7: charge % of each battery hand tool aboard (only with `SimOptions.battery`). */
  readonly toolCharge?: Readonly<Partial<Record<ToolKind, number>>>;
}

// ─── Input (renderer-agnostic) ──────────────────────────────────────────

export interface Input {
  /**
   * Desired movement direction in WORLD space (unit-ish vector, magnitude
   * 0..1 = throttle). The input layer converts screen-space joystick to world
   * (identity in top-down; camera-relative in 3D). {0,0} = stop (unless cruise).
   */
  moveX: number;
  moveY: number;
  /**
   * Drive-relative steering (the 3D view's default). When present the sim
   * ignores moveX/moveY: throttle −1..1 (+ forwards, − reverse), steer −1..1
   * (+ = right, heading increases). Reversing steers like a vehicle: steer
   * right while backing up swings the rear right.
   */
  drive?: { throttle: number; steer: number };
  /** Blades / trimmer / blower held. */
  toolHeld: boolean;
  /** −1, 0, +1 deck steps this frame (edge-triggered by the input layer). */
  deckDelta: number;
  /** Edge-triggered actions. */
  toggleCruise?: boolean;
  restartEngine?: boolean;
  switchTool?: ToolKind;
  /** Tarp: place at operator, or bag it. */
  placeTarp?: boolean;
  bagTarp?: boolean;
  /** Tap in world metres (collect a ground object within reach). */
  tapAt?: { x: number; y: number };
}

export const NO_INPUT: Input = { moveX: 0, moveY: 0, toolHeld: false, deckDelta: 0 };

export interface Sim {
  readonly state: SimState;
  step(dt: number, input: Input): void;
  /** Finish now (all done) or knock off early (abandoned). */
  result(abandoned?: boolean, opts?: ResultOptions): JobResult;
}

export interface ResultOptions {
  /**
   * Feature P5: pay the stripe bonus into `totalAud`. Off when absent, so the
   * result's money is the original formula; the shell applies it with
   * `withStripeBonus` when FEATURES.stripeScore is on.
   */
  stripeBonus?: boolean;
}

export interface SimOptions {
  layout: Layout;
  contract: Contract;
  /** The mower brought (a mower is always brought) plus owned-state to start from. */
  mower: OwnedEquipment;
  /** Other tools on the ute. */
  tools: OwnedEquipment[];
  /** Model catalogue lookup (so sim stays data-driven). */
  models: Record<string, EquipmentModel>;
  /**
   * Feature P2: give the mower its chute collision circle. Off when absent,
   * so existing callers (and the goldens) keep the old footprint; the game
   * passes FEATURES.chuteFootprint (on).
   */
  chuteFootprint?: boolean;
  /**
   * Feature P3: no mower came on the ute (a no-mow job). The operator starts on
   * foot with the first hand tool; `mower` is only a parked stand-in (at the
   * layout's `utePark` when it has one) that never moves, isn't in `loadout`
   * and isn't returned in `gear`. Off when absent.
   */
  walkOnly?: boolean;
  /**
   * Feature P6: debris rules. Leaves on beds and deck/step footprints count
   * against a clear; only the rake gets leaves out of a bed (the blower pushes
   * them further in); a catcher counts 100% toward clear; blowing leaves off
   * the property costs the clear task OFF_PROPERTY_NUDGE_PCT. Off when absent,
   * so existing callers keep the old scoring; the game passes FEATURES.debrisRules.
   */
  debrisRules?: boolean;
  /**
   * Feature P7: battery hand tools run on `chargePct` (BATTERY_RUN_S of
   * trigger time per full charge, flat at 0 = the tool stops), and powered
   * hand tools wear per minute of trigger time (battery ×1.6). Off when
   * absent, so existing callers keep the old sim; the game passes FEATURES.battery.
   */
  battery?: boolean;
  /**
   * Feature P9 flags, each off when absent so existing callers keep the old
   * sim; the game passes the matching FEATURES entry (sim/vibe.ts has the rules).
   *  - groundItems: ground checks spawn the named items (thong, sunnies, …);
   *    each wallet handed back pays HONEST_BLOKE_AUD.
   *  - tyreTracks: matted wheel tracks in `grid.matted`, fading over 30 s.
   *  - zeroTurn: a zero-turn pivots on the spot in either steering mode, with a
   *    rear-swing collision circle while it pivots and a first-pivot event.
   *  - extraMile: trimming a job with no trim task pays a one-off goodwill + rep.
   *  - slopeDrift: `layout.slope` drifts the mower downhill at speed.
   *  - trimOvercut: the trimmer cuts to TRIM_OVERCUT_MM on mowable cells.
   *  - passableProps: passable props bump instead of block; stone edging scatters.
   *  - blowBack: trimming beside paving throws clippings onto it (`grid.clip`).
   */
  groundItems?: boolean;
  tyreTracks?: boolean;
  zeroTurn?: boolean;
  extraMile?: boolean;
  slopeDrift?: boolean;
  trimOvercut?: boolean;
  passableProps?: boolean;
  blowBack?: boolean;
  /**
   * Crossed stripes: `grid.stripe2` keeps the heading a later pass crossed (at
   * 60° or more), so a checkerboard or diamond shows both sets, and the result
   * names the pattern (`JobResult.stripePattern`). Off when absent, so existing
   * callers keep the one-layer stripes; the game passes FEATURES.crossStripes.
   */
  crossStripes?: boolean;
}

// ─── Rendering ──────────────────────────────────────────────────────────

export interface Renderer {
  mount(el: HTMLElement): void;
  render(state: SimState): void;
  resize(): void;
  unmount(): void;
  /** Screen → world (metres), for taps and joystick-to-world conversion. */
  screenToWorld?(sx: number, sy: number): { x: number; y: number };
  /** Rotation of the camera around the vertical axis (0 in top-down). */
  cameraYaw?(): number;
}
